/**
 * Which brand this portal wears.
 *
 * One codebase serves several portals — Green Giraffe in India, TVOICE in
 * Africa and Europe — and until now the brand was a single hard-coded object,
 * so every portal called itself Green Giraffe regardless of who was looking.
 *
 * The brand is resolved from the HOSTNAME rather than baked in at build time.
 * That is deliberate: the same bundle can then be deployed to every portal and
 * still greet each audience correctly, which is the point of unifying them. A
 * build-time variable would have put us back to one build per region, which is
 * exactly the arrangement that let a Green Giraffe build be deployed over the
 * Africa portal once already.
 *
 * REACT_APP_BRAND overrides the hostname when set, for local work and for any
 * deployment whose domain does not carry its own name.
 */

export type BrandKey = 'gg' | 'tvoice' | 'cmc';

export interface BrandConfig {
  brandName: string;
  /** Small line under the brand name, e.g. "by Teeoff Technologies Private Limited". */
  brandOwner: string;
  brandTagline: string;
  loginTitle: string;
  loginSubtitle: string;
  logo: {
    imageUrl: string;
    iconName: string;
    iconColor: string;
    iconBgColor: string;
  };
  background: {
    showBrandName: boolean;
    showTagline: boolean;
    gradient: { from: string; via: string; to: string };
  };
  responsive: { mobileLayout: string };
  footer: { showPoweredBy: boolean; companyName: string; year: number };
  /**
   * Who the privacy policy is BY, and who a reader contacts about it.
   *
   * <p>Brand-wise because the portal is resold: a CatchMyCall customer reading a
   * policy signed "Green Giraffe" has been told their data is held by a company
   * they have no relationship with. The policy TEXT is shared — the product is one
   * product — and only the party and its contact details differ.
   */
  privacy: {
    /** The legal entity, as it should appear in a policy. */
    legalName: string;
    /** Where a privacy question goes. Must be a real, monitored address. */
    contactEmail: string;
    /** Postal address, for jurisdictions that require one. */
    address: string;
    /** Shown to the reader so they can tell whether they have seen this version. */
    lastUpdated: string;
  };
  /**
   * The MCP connector — the thing a third-party assistant connects to.
   *
   * <p>Brand-wise for the same reason the policy is: the docs page says who the
   * connector is FROM and where support goes, and a reader on a reseller's portal
   * must not be handed another company's support address.
   *
   * <p><b>`serverUrl` is one deployment, not one per brand.</b> There is a single
   * `cpaas-mcp` service and the OAuth issuer is baked into every client registered
   * against it, so changing the URL re-registers them all. A brand that is not
   * offering the connector sets `enabled: false` and the docs page says so, rather
   * than printing a URL that belongs to somebody else's listing.
   */
  connector: {
    /** Whether this brand offers the connector at all. */
    enabled: boolean;
    /** The MCP endpoint an assistant is pointed at. Include the path. */
    serverUrl: string;
    /** Where a connector question goes. Must be a real, monitored address. */
    supportEmail: string;
    /** The directory listing's slug, for the docs page to echo back. */
    slug: string;
    /**
     * The listing icon: a SQUARE image on an opaque ground.
     *
     * <p>Square because a directory card crops to one, and opaque because this
     * mark's interior is transparent — on a dark card the sound-wave arcs
     * disappear into the background and the mic sits in a muddy field. The
     * portal's own copy of the mark keeps its transparency; this one does not.
     */
    iconUrl: string;
  };
  terms: {
    enabled: boolean;
    autoAccept: boolean;
    text: string;
    linkText: string;
    url: string;
    privacyText: string;
    privacyUrl: string;
    showPrivacy: boolean;
  };
}

/** Everything the two brands agree on. Only the identity fields differ. */
const SHARED = {
  loginTitle: 'Welcome Back',
  loginSubtitle: 'Sign in to access your communication dashboard',
  logo: {
    // Place a file in public/ and reference it as '/logo.png', or use a full URL.
    imageUrl: '',
    iconName: 'MessageSquare',
    iconColor: 'text-blue-700',
    iconBgColor: 'bg-blue-100',
  },
  background: {
    showBrandName: true,
    showTagline: true,
    // Flat tinted grounds rather than gradients — these stops sit within one hue
    // so the hero reads as a solid ground with only a whisper of depth. Keep
    // them adjacent steps if you change them.
    gradient: {
      from: 'from-blue-900',
      via: 'via-blue-900',
      to: 'to-indigo-900',
    },
  },
  responsive: {
    // On mobile and tablet the brand sits above the login card; from lg it moves
    // to the left with the card on the right.
    mobileLayout: 'stacked',
  },
  privacy: {
    legalName: 'Teeoff Technologies Private Limited',
    contactEmail: 'contactus@teeofftechnologies.com',
    // The registered address as published in CatchMyCall's own privacy policy at
    // catchmycall.ai/privacy-policy, which names Teeoff as the data controller.
    // Previously just "Gurgaon, Haryana, India" — a city is not an address a
    // data subject can write to, and several jurisdictions require a postal one.
    //
    // Shared by every brand on purpose, and confirmed 2026-10-06: Green Giraffe
    // Private Limited is registered at the same address as Teeoff. They are
    // distinct companies with one registered office, so `legalName` stays
    // per-brand while this does not.
    address:
      'D-102, The Palm Drive, Golf Course Extension Road, Sector-66, Gurgaon, Haryana, India - 122018',
    lastUpdated: '2 October 2026',
  },
  // Off by default. A brand earns a connector listing by having one, and the
  // failure mode of defaulting to `true` is a Green Giraffe docs page telling
  // readers to connect to CatchMyCall's server.
  connector: {
    enabled: false,
    serverUrl: 'https://mcp.catchmycall.ai/mcp',
    supportEmail: 'contactus@teeofftechnologies.com',
    slug: '',
    iconUrl: '',
  },
  terms: {
    enabled: true,
    autoAccept: true,
    text: 'I agree to the',
    linkText: 'Terms and Conditions',
    url: '/terms',
    privacyText: 'Privacy Policy',
    privacyUrl: '/privacy',
    showPrivacy: true,
  },
};

export const BRANDS: Record<BrandKey, BrandConfig> = {
  gg: {
    ...SHARED,
    brandName: 'Green Giraffe',
    brandOwner: '',
    privacy: {
      ...SHARED.privacy,
      legalName: 'Green Giraffe Private Limited',
    },
    brandTagline: 'Elevating Business Communication',
    footer: {
      showPoweredBy: true,
      companyName: 'Green Giraffe Private Limited',
      year: new Date().getFullYear(),
    },
  },
  /**
   * CatchMyCall — a reseller whose businesses (Atmantan and others) see its name
   * rather than ours.
   *
   * This entry exists for the LOGIN screen only. After sign-in the brand comes
   * from the service-provider profile in the database (see PortalProfileContext),
   * which is what makes adding the next reseller a row rather than a deploy.
   * Before sign-in there is no organization and therefore no provider, so the
   * hostname is the only thing to go on — without this, their people would see
   * another brand at login and CatchMyCall immediately after.
   */
  cmc: {
    ...SHARED,
    brandName: 'CatchMyCall',
    brandOwner: '',
    /**
     * CatchMyCall is a TRADING NAME of Teeoff, not a separate company.
     *
     * <p>This said `legalName: 'CatchMyCall'`, on the reasoning that signing a
     * reseller's policy with the platform owner's name would name a company their
     * customer had never dealt with. That reasoning is sound and did not apply
     * here: CatchMyCall's own published policy opens "...how Teeoff Technologies
     * Private Limited ('CatchMyCall', 'we', 'us', or 'our') collects...", and
     * names Teeoff as the data controller with its registered address.
     *
     * <p>So the portal was signing its policy as an entity that does not exist,
     * and contradicting the public policy for the same product. Inheriting SHARED
     * is correct, and the pages now read "CatchMyCall is provided by Teeoff
     * Technologies Private Limited" — brand and entity, which is what the two
     * fields are for.
     */
    privacy: { ...SHARED.privacy },
    // The one brand with a connector today: `mcp.catchmycall.ai` is the live
    // deployment and the directory submission is in this name.
    connector: {
      ...SHARED.connector,
      enabled: true,
      supportEmail: 'support@catchmycall.ai',
      slug: 'catchmycall',
      iconUrl: '/brand/catchmycall-connector-icon.png',
    },
    /**
     * The MARK only, not the full lockup.
     *
     * <p>The horizontal logo on the website is mark + wordmark, and the login
     * screen prints `brandName` as text immediately to its right — so the
     * wordmark would say "CatchMyCall" twice, and at the 40px the lockup renders
     * at, the wordmark is unreadable anyway.
     */
    logo: {
      ...SHARED.logo,
      imageUrl: '/brand/catchmycall-mark.png',
    },
    brandTagline: 'Never miss a customer call',
    footer: {
      showPoweredBy: true,
      companyName: 'CatchMyCall',
      year: new Date().getFullYear(),
    },
  },
  tvoice: {
    ...SHARED,
    brandName: 'TVOICE',
    brandOwner: 'by Teeoff Technologies Private Limited',
    brandTagline: 'Elevating Business Communication',
    footer: {
      showPoweredBy: true,
      companyName: 'Teeoff Technologies Private Limited',
      year: new Date().getFullYear(),
    },
  },
};

/**
 * Hostnames matched in order, first match wins.
 *
 * Matching is on a substring of the host, so it covers the staging and app
 * variants of each domain without listing them one by one. Anything unmatched
 * falls back to Green Giraffe, which is what an unrecognised host served
 * before this existed.
 */
const HOST_BRANDS: Array<[RegExp, BrandKey]> = [
  // Longest/most specific first — 'catchmycall' must be tested before any
  // broader pattern that could also match its host.
  [/catchmycall/i, 'cmc'],
  [/tvoice/i, 'tvoice'],
  [/teeoff\.tech/i, 'tvoice'],
  [/greengiraffe/i, 'gg'],
];

export const DEFAULT_BRAND: BrandKey = 'gg';

/** Exported for tests: resolve a brand key from an explicit host and override. */
export const resolveBrandKey = (
  hostname: string,
  override?: string,
): BrandKey => {
  const wanted = (override || '').trim().toLowerCase();
  // Checked against the BRANDS map rather than a hand-written list of keys. The
  // list said `gg | tvoice` and was never extended when `cmc` was added, so
  // REACT_APP_BRAND=cmc fell through to hostname matching and silently resolved
  // to Green Giraffe — which is exactly the brand you are NOT trying to preview
  // when you set the variable. A new brand now works without touching this.
  if (wanted && wanted in BRANDS) return wanted as BrandKey;

  for (const [pattern, key] of HOST_BRANDS) {
    if (pattern.test(hostname)) return key;
  }
  return DEFAULT_BRAND;
};

export const ACTIVE_BRAND: BrandKey = resolveBrandKey(
  typeof window !== 'undefined' && window.location ? window.location.hostname : '',
  process.env.REACT_APP_BRAND,
);

/**
 * The resolved brand. Call sites read this exactly as they did when it was a
 * single literal, so nothing outside this file had to change.
 */
export const BRAND_CONFIG: BrandConfig = BRANDS[ACTIVE_BRAND];
